import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Inbox,
  RefreshCw,
  Send,
  Lock,
  Unlock,
  Trash2,
  MessageCircle,
  User as UserIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 5000;

const formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('az-AZ', {
      month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const SupportInbox = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all' | 'open' | 'closed'
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchSessions = useCallback(async () => {
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const res = await axios.get(`${API}/admin/support/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setSessions(res.data?.items || []);
      setTotalUnread(res.data?.total_unread || 0);
    } catch (e) { /* silent */ }
  }, [token, filter]);

  const fetchSession = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const res = await axios.get(`${API}/admin/support/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedSession(res.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchSessions();
    pollRef.current = setInterval(fetchSessions, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchSessions]);

  useEffect(() => {
    if (selectedId) {
      fetchSession(selectedId);
      const t = setInterval(() => fetchSession(selectedId), POLL_INTERVAL_MS);
      return () => clearInterval(t);
    }
  }, [selectedId, fetchSession]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!draft.trim() || !selectedId || sending) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/support/reply`,
        { session_id: selectedId, text: draft.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDraft('');
      fetchSession(selectedId);
      fetchSessions();
    } catch (e) {
      alert(e.response?.data?.detail || 'Göndərilmədi');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!selectedId) return;
    try {
      await axios.post(`${API}/admin/support/close`, { session_id: selectedId },
        { headers: { Authorization: `Bearer ${token}` } });
      fetchSession(selectedId);
      fetchSessions();
    } catch (e) { /* silent */ }
  };

  const handleReopen = async () => {
    if (!selectedId) return;
    try {
      await axios.post(`${API}/admin/support/reopen`, { session_id: selectedId },
        { headers: { Authorization: `Bearer ${token}` } });
      fetchSession(selectedId);
      fetchSessions();
    } catch (e) { /* silent */ }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!confirm('Bu sessiyanı tamamilə silmək istəyirsiniz?')) return;
    try {
      await axios.delete(`${API}/admin/support/sessions/${selectedId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setSelectedId(null);
      setSelectedSession(null);
      fetchSessions();
    } catch (e) { /* silent */ }
  };

  return (
    <div data-testid="support-inbox" style={{ marginTop: 28 }}>
      <h3 className="admin-section-title flex items-center gap-2">
        <Inbox size={16} /> Dəstək mesajları
        {totalUnread > 0 && (
          <span
            data-testid="support-inbox-unread-total"
            style={{
              background: '#ef4444',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 10,
            }}
          >
            {totalUnread}
          </span>
        )}
      </h3>
      <p className="admin-section-desc">
        Ziyarətçilərdən gələn çat mesajları. Hər 5 saniyədə avtomatik yenilənir.
      </p>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, alignItems: 'center' }}>
        {[
          { id: 'all', label: 'Hamısı' },
          { id: 'open', label: 'Açıq' },
          { id: 'closed', label: 'Bağlı' },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`admin-tab ${filter === f.id ? 'admin-tab-active' : ''}`}
            data-testid={`support-filter-${f.id}`}
            style={{ padding: '6px 12px' }}
          >
            {f.label}
          </button>
        ))}
        <button type="button" onClick={fetchSessions} className="admin-btn-secondary" style={{ marginLeft: 8 }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Two-pane layout */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 280px) 1fr',
          gap: 12,
          minHeight: 420,
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        {/* Sessions list */}
        <div
          style={{
            background: 'rgba(0,0,0,0.2)',
            borderRight: '1px solid rgba(255,255,255,0.05)',
            overflowY: 'auto',
            maxHeight: 540,
          }}
        >
          {sessions.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
              Hələ heç bir mesaj yoxdur
            </div>
          ) : (
            sessions.map((s) => {
              const isSelected = s.id === selectedId;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSelectedId(s.id)}
                  data-testid={`support-session-${s.id}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 14px',
                    background: isSelected ? 'rgba(168,85,247,0.15)' : 'transparent',
                    border: 'none',
                    borderLeft: `3px solid ${isSelected ? '#a855f7' : 'transparent'}`,
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    cursor: 'pointer',
                    color: '#fff',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                      <UserIcon size={12} style={{ color: 'rgba(255,255,255,0.5)' }} />
                      {s.name}
                    </div>
                    {s.unread_for_admin > 0 && (
                      <span
                        style={{
                          background: '#ef4444',
                          color: '#fff',
                          fontSize: 10,
                          fontWeight: 700,
                          minWidth: 18,
                          height: 18,
                          padding: '0 5px',
                          borderRadius: 9,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {s.unread_for_admin}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>
                    {formatTimestamp(s.last_user_message_at || s.created_at)}
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    {s.status === 'closed' && (
                      <span style={{
                        fontSize: 9, padding: '1px 6px', borderRadius: 8,
                        background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)',
                      }}>BAĞLI</span>
                    )}
                    {s.whatsapp && (
                      <span style={{
                        fontSize: 9, padding: '1px 6px', borderRadius: 8,
                        background: 'rgba(34,197,94,0.1)', color: '#86efac',
                      }}>WA</span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Thread */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 420, maxHeight: 540 }}>
          {!selectedSession ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.3)', fontSize: 13,
            }}>
              <MessageCircle size={32} style={{ opacity: 0.3, marginRight: 10 }} />
              Soldan bir sessiya seçin
            </div>
          ) : (
            <>
              {/* Session header */}
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  background: 'rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
                    {selectedSession.name}
                  </div>
                  {selectedSession.whatsapp && (
                    <a
                      href={`https://wa.me/${selectedSession.whatsapp.replace(/[+\s]/g, '')}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 11, color: '#86efac', textDecoration: 'underline' }}
                      data-testid="support-thread-wa-link"
                    >
                      {selectedSession.whatsapp}
                    </a>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {selectedSession.status === 'open' ? (
                    <button type="button" onClick={handleClose} className="admin-btn-secondary" data-testid="support-thread-close-btn">
                      <Lock size={12} /> Bağla
                    </button>
                  ) : (
                    <button type="button" onClick={handleReopen} className="admin-btn-secondary" data-testid="support-thread-reopen-btn">
                      <Unlock size={12} /> Yenidən aç
                    </button>
                  )}
                  <button type="button" onClick={handleDelete} className="admin-btn-secondary" style={{ color: '#fca5a5' }} data-testid="support-thread-delete-btn">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {(selectedSession.messages || []).map((m) => (
                  <div
                    key={m.id}
                    style={{
                      alignSelf: m.sender === 'admin' ? 'flex-end' : 'flex-start',
                      maxWidth: '80%',
                    }}
                  >
                    <div
                      style={{
                        background:
                          m.sender === 'admin'
                            ? 'linear-gradient(135deg, rgba(168,85,247,0.7) 0%, rgba(109,40,217,0.5) 100%)'
                            : 'rgba(255,255,255,0.06)',
                        border:
                          m.sender === 'admin'
                            ? '1px solid rgba(168,85,247,0.5)'
                            : '1px solid rgba(255,255,255,0.08)',
                        color: '#fff',
                        padding: '8px 12px',
                        borderRadius:
                          m.sender === 'admin' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                        fontSize: 13,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {m.text}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: 'rgba(255,255,255,0.35)',
                        marginTop: 3,
                        textAlign: m.sender === 'admin' ? 'right' : 'left',
                      }}
                    >
                      {formatTimestamp(m.created_at)}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              {selectedSession.status === 'open' ? (
                <form
                  onSubmit={handleSend}
                  style={{
                    padding: 10,
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    gap: 6,
                  }}
                >
                  <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Cavabınızı yazın..."
                    maxLength={2000}
                    data-testid="support-thread-input"
                    style={{
                      flex: 1,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                    }}
                  />
                  <button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    data-testid="support-thread-send-btn"
                    style={{
                      background: 'linear-gradient(135deg, #a855f7 0%, #6d28d9 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      width: 44,
                      cursor: sending || !draft.trim() ? 'not-allowed' : 'pointer',
                      opacity: sending || !draft.trim() ? 0.5 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Send size={16} />
                  </button>
                </form>
              ) : (
                <div style={{
                  padding: 12, borderTop: '1px solid rgba(255,255,255,0.08)',
                  fontSize: 12, color: 'rgba(255,255,255,0.4)', textAlign: 'center',
                }}>
                  Bu sessiya bağlanıb
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportInbox;
